import {useState} from "react";
import {
	companyIdRefused, companyOnAccount, DISCARDED_SENDER, freshSender, type CompanyFields, type SenderChoice,
} from "sender/senderChoice";

/** Who files are sent as, and the controls the Personalise builder's "Your name" offers for it. */
export interface SenderChoiceControls {
	sender: SenderChoice;
	/** Sends as a person or as the company; the company typed is kept either way. */
	choose: (kind: SenderChoice["kind"]) => void;
	setCompany: (company: CompanyFields) => void;
	/** The company is chosen and its tax ID is one the api would refuse (`companyIdRefused`). */
	companyIdInvalid: boolean;
	/** The builder was discarded: a person again, the company cleared (owner 2026-10-02, `DISCARDED_SENDER`). */
	discard: () => void;
	/** A new transfer begins ("Send another", "Start over"): a person again, the account's company ready. */
	reset: () => void;
}

/**
 * Who the home panel's transfer or request goes out as (kitecorp/transferify-ui#3140, #3186): a person or a company,
 * chosen beside the Personalise builder's "Your name". Held by the create flow rather than the builder, the panel or
 * either composer, so the choice outlives the builder closing and opening again, the panel turning between sending
 * and asking, and a send that is cancelled or fails and goes back to the panel. A thin adapter over
 * `sender/senderChoice`.
 *
 * `accountCompany` is the account's own company, if it has one, which the company choice starts from; one read
 * later, as when a visitor signs in through the composer's code step, still fills a name nobody has typed.
 */
export function useSenderChoice(accountCompany: string): SenderChoiceControls {
	const [sender, setSender] = useState<SenderChoice>(() => freshSender(accountCompany));
	// Set while rendering, React's pattern for state that follows a prop.
	const [accountCompanySeen, setAccountCompanySeen] = useState(accountCompany);
	if (accountCompany !== accountCompanySeen) {
		setAccountCompanySeen(accountCompany);
		setSender(current => ({...current, company: companyOnAccount(current.company, accountCompany)}));
	}
	return {
		sender,
		choose: kind => setSender(current => ({...current, kind})),
		setCompany: company => setSender(current => ({...current, company})),
		companyIdInvalid: companyIdRefused(sender),
		discard: () => setSender(DISCARDED_SENDER),
		reset: () => setSender(freshSender(accountCompany)),
	};
}
